/**
 * Scroll Arrow Module
 * スクロールに応じて伸びる曲がりくねった矢印
 */
export class ScrollArrow {
  constructor() {
    this.elements = document.querySelectorAll('.js-scroll-arrow');
    this.scrollHandler = null;
    this.scrollTimer = null;
    this.elementData = new Map();
    
    if (this.elements.length > 0) {
      this.init();
    }
  }
  
  init() {
    const scrollTop = window.pageYOffset || document.documentElement.scrollTop;
    
    // 各要素の初期位置とパス情報を保存
    this.elements.forEach((element) => {
      const svg = element.querySelector('svg');
      const path = element.querySelector('path');
      const arrowHead = element.querySelector('.js-scroll-arrow__head');
      if (!path || !svg) return;
      
      const rect = element.getBoundingClientRect();
      const pathLength = path.getTotalLength();
      
      // パスを細かく分割して線分の配列を作成（均一な太さを保つため）
      const segments = this.createPathSegments(path, pathLength);
      
      // 元のパスを非表示にして、線分用のグループを作成
      path.style.display = 'none';
      const lineGroup = document.createElementNS('http://www.w3.org/2000/svg', 'g');
      lineGroup.classList.add('p-top-scroll-arrow__lines');
      svg.appendChild(lineGroup);
      
      // 各セグメントを線分として追加
      segments.forEach((segment, index) => {
        const line = document.createElementNS('http://www.w3.org/2000/svg', 'line');
        line.setAttribute('x1', segment.x1);
        line.setAttribute('y1', segment.y1);
        line.setAttribute('x2', segment.x2);
        line.setAttribute('y2', segment.y2);
        line.classList.add('p-top-scroll-arrow__line');
        line.style.opacity = '0';
        lineGroup.appendChild(line);
      });
      
      // パスの終点位置を取得して矢印の先端を配置
      if (arrowHead) {
        const endPoint = path.getPointAtLength(pathLength);
        arrowHead.setAttribute('cx', endPoint.x);
        arrowHead.setAttribute('cy', endPoint.y);
      }
      
      this.elementData.set(element, {
        top: rect.top + scrollTop,
        pathLength: pathLength,
        path: path,
        arrowHead: arrowHead,
        segments: segments,
        lineGroup: lineGroup
      });
    });
    
    this.updateArrow();
    this.setupScrollHandler();
    this.setupResizeHandler();
  }
  
  /**
   * パスを細かく分割して線分の配列を作成
   */
  createPathSegments(path, pathLength) {
    const segments = [];
    const segmentCount = Math.ceil(pathLength / 2); // 2pxごとに分割
    
    for (let i = 0; i < segmentCount; i++) {
      const t1 = i / segmentCount;
      const t2 = (i + 1) / segmentCount;
      
      const point1 = path.getPointAtLength(pathLength * t1);
      const point2 = path.getPointAtLength(pathLength * t2);
      
      segments.push({
        x1: point1.x,
        y1: point1.y,
        x2: point2.x,
        y2: point2.y
      });
    }
    
    return segments;
  }
  
  /**
   * スクロールハンドラーを設定（スロットル付き）
   */
  setupScrollHandler() {
    this.scrollHandler = () => {
      if (this.scrollTimer) {
        return;
      }
      
      this.scrollTimer = requestAnimationFrame(() => {
        this.updateArrow();
        this.scrollTimer = null;
      });
    };
    
    window.addEventListener('scroll', this.scrollHandler, { passive: true });
  }
  
  /**
   * リサイズハンドラーを設定（デバウンス付き）
   */
  setupResizeHandler() {
    let resizeTimer;
    const resizeHandler = () => {
      clearTimeout(resizeTimer);
      resizeTimer = setTimeout(() => {
        const scrollTop = window.pageYOffset || document.documentElement.scrollTop;
        
        this.elements.forEach((element) => {
          const path = element.querySelector('path');
          const arrowHead = element.querySelector('.js-scroll-arrow__head');
          if (!path) return;
          
          const rect = element.getBoundingClientRect();
          const pathLength = path.getTotalLength();
          const data = this.elementData.get(element);
          
          if (data) {
            // パスを再分割
            const segments = this.createPathSegments(path, pathLength);
            
            // 既存の線分グループを削除して再作成
            if (data.lineGroup) {
              data.lineGroup.remove();
            }
            
            const svg = element.querySelector('svg');
            const lineGroup = document.createElementNS('http://www.w3.org/2000/svg', 'g');
            lineGroup.classList.add('p-top-scroll-arrow__lines');
            svg.appendChild(lineGroup);
            
            segments.forEach((segment) => {
              const line = document.createElementNS('http://www.w3.org/2000/svg', 'line');
              line.setAttribute('x1', segment.x1);
              line.setAttribute('y1', segment.y1);
              line.setAttribute('x2', segment.x2);
              line.setAttribute('y2', segment.y2);
              line.classList.add('p-top-scroll-arrow__line');
              line.style.opacity = '0';
              lineGroup.appendChild(line);
            });
            
            // パスの終点位置を再計算して矢印の先端を配置
            if (arrowHead) {
              const endPoint = path.getPointAtLength(pathLength);
              arrowHead.setAttribute('cx', endPoint.x);
              arrowHead.setAttribute('cy', endPoint.y);
            }
            
            this.elementData.set(element, {
              ...data,
              top: rect.top + scrollTop,
              pathLength: pathLength,
              segments: segments,
              lineGroup: lineGroup
            });
          }
        });
        
        this.updateArrow();
      }, 100);
    };
    
    window.addEventListener('resize', resizeHandler, { passive: true });
  }
  
  /**
   * 矢印の描画を更新
   */
  updateArrow() {
    const scrollTop = window.pageYOffset || document.documentElement.scrollTop;
    const windowHeight = window.innerHeight;
    
    this.elements.forEach((element) => {
      const data = this.elementData.get(element);
      if (!data) return;
      
      const { top: elementTop, pathLength, path, segments, lineGroup, arrowHead } = data;
      
      // 要素がビューポートに入る位置を計算
      const startScrollTop = elementTop - windowHeight;
      const endScrollTop = elementTop;
      
      // スクロール位置に応じた進捗率を計算（0〜1）
      let progress = 0;
      if (scrollTop >= startScrollTop && scrollTop <= endScrollTop) {
        progress = (scrollTop - startScrollTop) / (endScrollTop - startScrollTop);
      } else if (scrollTop > endScrollTop) {
        progress = 1;
      }
      
      // 進捗率を0〜1の範囲に制限
      progress = Math.max(0, Math.min(1, progress));
      
      // 表示するセグメント数を計算
      const visibleSegmentCount = Math.floor(segments.length * progress);
      
      // 各線分の表示/非表示を更新
      const lines = lineGroup.querySelectorAll('.p-top-scroll-arrow__line');
      lines.forEach((line, index) => {
        if (index < visibleSegmentCount) {
          line.style.opacity = '1';
        } else {
          line.style.opacity = '0';
        }
      });
      
      // 矢印の先端をパスの現在の描画終点位置に正確に移動
      if (arrowHead) {
        if (progress > 0) {
          // 実際に描画されているパスの終点位置を計算
          const drawnLength = pathLength * progress;
          const currentPoint = path.getPointAtLength(drawnLength);
          arrowHead.setAttribute('cx', currentPoint.x);
          arrowHead.setAttribute('cy', currentPoint.y);
          arrowHead.style.opacity = '1';
        } else {
          arrowHead.style.opacity = '0';
        }
      }
    });
  }
  
  /**
   * イベントリスナーを削除
   */
  destroy() {
    if (this.scrollHandler) {
      window.removeEventListener('scroll', this.scrollHandler);
      this.scrollHandler = null;
    }
    
    if (this.scrollTimer) {
      cancelAnimationFrame(this.scrollTimer);
      this.scrollTimer = null;
    }
  }
}
